import React, { useCallback, useEffect, useRef } from 'react';
import { Grid as BryntumGrid } from '@bryntum/schedulerpro';
import { useSchedulerContext } from '../context/SchedulerContext';
import { useDragDrop } from '../hooks/useDragDrop';
import { PendingSchedulerEvent } from '../types/scheduler.types';

export interface DragDropSetupProps {
  /** Reference to the unassigned tasks grid */
  gridRef: React.RefObject<{ instance?: BryntumGrid }>;
  /** Timezone for date formatting during drag operations */
  timeZone: string;
  /** Callback when a task is dropped onto the scheduler */
  onDrop: (params: {
    taskId: string;
    resourceId: string;
    startDate: string;
    endDate: string;
  }) => Promise<void>;
  /** Whether drag-drop is enabled (usually based on permissions) */
  enabled: boolean;
  /** Callback to set/clear the pending event for optimistic UI */
  onPendingEventChange?: (event: PendingSchedulerEvent | null) => void;
  /** Callback to show error notification on invalid drop */
  onInvalidDrop?: (message: string) => void;
}

/**
 * Helper component that sets up drag-drop functionality between the
 * unassigned tasks grid and the scheduler.
 *
 * This component uses the useDragDrop hook to configure Bryntum's
 * DragHelper for dragging tasks from the unassigned grid onto
 * scheduler resource rows.
 *
 * Must be rendered inside SchedulerProvider.
 */
export function DragDropSetup({
  gridRef,
  timeZone,
  onDrop,
  enabled,
  onPendingEventChange,
  onInvalidDrop,
}: DragDropSetupProps) {
  const { schedulerRef } = useSchedulerContext();
  const pendingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Clear pending timer on unmount to avoid firing after teardown
  useEffect(() => {
    return () => {
      if (pendingTimerRef.current) {
        clearTimeout(pendingTimerRef.current);
      }
    };
  }, []);

  // Wrap onDrop to clear pending event after API completes
  const wrappedOnDrop = useCallback(
    async (params: {
      taskId: string;
      resourceId: string;
      startDate: string;
      endDate: string;
    }) => {
      try {
        await onDrop(params);
      } finally {
        // Clear the pending placeholder after API completes (success or failure)
        // Small delay to ensure the real data has been loaded
        pendingTimerRef.current = setTimeout(() => {
          pendingTimerRef.current = null;
          onPendingEventChange?.(null);
        }, 500);
      }
    },
    [onDrop, onPendingEventChange]
  );

  useDragDrop({
    schedulerRef,
    gridRef,
    timeZone,
    onDrop: wrappedOnDrop,
    enabled,
    onPendingEventChange,
    onInvalidDrop,
  });

  return null;
}
